import React, { useMemo } from 'react';
import { InternalApiScenarioLanguage } from '../../api-internal';
import { SalvDropdown, SalvFieldHeader, SalvTextField, Size } from '@salvapp/ui-components';
import { convertToDropdownOption } from '../../common/helper';
import { useFormikContext } from 'formik';
import { internalRiskRuleApi, querytemplateApi } from '../../apis';
import Editor from '../../common/editor/Editor';
import { useQuery } from '@tanstack/react-query';
import { useMe } from '../../organisation/useMe';
import { getCustomStatementsSQLHighlightingMode } from '../../common/editor/editor-modes';
import { Ace } from 'ace-builds';
import { RiskRuleVersionModalFormValues } from './RiskRuleVersionModal';
import { getComputedParametersCompleter, getQueryTemplateCompleter } from '../../common/versioning/helper';
import './RiskRuleVersionRightPanel.scss';

export interface RiskRuleVersionRightPanelProps {
  handle: string;
  isDisabled: boolean;
}

const RiskRuleVersionRightPanel = ({ handle, isDisabled }: RiskRuleVersionRightPanelProps) => {
  const { setFieldValue, values } = useFormikContext<RiskRuleVersionModalFormValues>();

  const currentUser = useMe();

  const { data: queryTemplateNames } = useQuery({
    queryKey: ['query_template_names', currentUser.currentOrganisation?.id],
    queryFn: async () => await querytemplateApi.findAllQueryTemplateNames(),
    initialData: [],
  });

  const { data: computedParameters } = useQuery({
    queryKey: ['computed_parameter_names', currentUser.currentOrganisation?.id, handle],
    queryFn: async () => await internalRiskRuleApi.getRiskRuleParameters({ handle: handle }),
    initialData: [],
  });

  const highlightedStatements = useMemo(() => {
    const statements: Ace.HighlightRule[] = [];

    // Matching priority 2
    const fixedVariables = computedParameters.map((p) => p.name).join('|');
    statements.push({
      token: 'keyword.fixed_variable',
      regex: `\\$(?:${fixedVariables})\\b`,
    });

    // Matching priority 3
    if (queryTemplateNames.length) {
      const queryTemplateVariables = queryTemplateNames.join('|');
      statements.push({
        token: 'keyword.query_template',
        regex: `\\$(?:${queryTemplateVariables})\\b`,
      });
    }
    return statements;
  }, [queryTemplateNames, computedParameters]);

  return (
    <div className='risk-rule-version-right-panel'>
      <div>
        <SalvTextField
          label='Details'
          description='Define the information that will appear in person risk assessment (use parameters if needed)'
          disabled={isDisabled}
          required={true}
          onChange={(value) => setFieldValue('details', value)}
          value={values.details}
          size={Size.SMALL}
        />
      </div>

      <div className='border-0 risk-rule-query-editor-container'>
        <div>
          <SalvFieldHeader label='Query' size={Size.MEDIUM} />
          <SalvDropdown
            placeholder='Select...'
            disabled={isDisabled}
            options={Object.entries(InternalApiScenarioLanguage)
              .filter(([key]) => !(key === InternalApiScenarioLanguage.JMESPATH))
              .map(([key, value]) => ({
                key: key,
                value: value,
                label: value,
              }))}
            size={Size.SMALL}
            clearable={false}
            value={convertToDropdownOption(InternalApiScenarioLanguage.SQL, InternalApiScenarioLanguage.SQL)}
            className='query-language-field'
            onChange={(option) => {
              setFieldValue('parameters', []);
              setFieldValue('language', option[0].value);
            }}
            ariaLabel='language'
          />
        </div>

        <div data-testid='scenario-query-editor'>
          <Editor
            disabled={isDisabled}
            value={values.query}
            onChange={(query: string) => setFieldValue('query', query)}
            type={InternalApiScenarioLanguage.SQL}
            syntaxHighlight={!isDisabled}
            enableLiveAutocompletion={true}
            highlightingMode={getCustomStatementsSQLHighlightingMode(highlightedStatements)}
            completers={[
              getComputedParametersCompleter(computedParameters),
              getQueryTemplateCompleter(queryTemplateNames),
            ]}
            withBorder={true}
            maxLines={values.query.split('\n').length + 2}
          />
        </div>
      </div>
    </div>
  );
};

export default RiskRuleVersionRightPanel;
